import React, { useState, useEffect } from "react";
import { Input, InputProps, message } from "antd";
import { positiveDecimalRegex } from '../../utils/numberValidation';

interface NumberInputProps extends Omit<InputProps, 'onChange' | 'value' | 'onBlur'> {
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  decimalPlaces?: number;
  placeholder?: string;
  size?: "small" | "middle" | "large";
  style?: React.CSSProperties;
  isInteger?: boolean;
  /** When true, rejects negative values — only positive decimals are accepted. */
  positiveOnly?: boolean;
  /**
   * When true, the integer part must be exactly "0" — only values like 0.888 are accepted.
   * Only applies when positiveOnly is true.
   */
  zeroIntegerOnly?: boolean;
  /**
   * Limits the number of digits allowed before the decimal point.
   * E.g. maxIntegerDigits={1} allows "0.888" and "9.123" but not "88.888".
   * Only applies when positiveOnly is true and zeroIntegerOnly is not set.
   */
  maxIntegerDigits?: number;
}

// Max digits allowed in the integer part app-wide. Decimal digits are governed
// solely by the existing per-instance `decimalPlaces` prop (default 3) — not by
// a separate cap here.
const MAX_INTEGER_DIGITS = 9;
const MAX_DIGITS_MESSAGE_KEY = 'number-input-max-digits';
const NEGATIVE_NOT_ALLOWED_MESSAGE_KEY = 'number-input-negative-not-allowed';

const countDigits = (str: string): number => (str.match(/\d/g) || []).length;

// Whether `val` (a valid, in-progress numeric string, optionally signed) has more
// than MAX_INTEGER_DIGITS digits before the decimal point.
const exceedsIntegerDigitCap = (val: string): boolean => {
  const intPart = val.replace('-', '').split('.')[0] || '';
  return countDigits(intPart) > MAX_INTEGER_DIGITS;
};

const warnMaxDigits = () => {
  message.warning({
    content: `Maximum ${MAX_INTEGER_DIGITS} digits allowed before the decimal point.`,
    key: MAX_DIGITS_MESSAGE_KEY,
  });
};

const warnNegativeNotAllowed = () => {
  message.warning({
    content: 'Negative values are not allowed.',
    key: NEGATIVE_NOT_ALLOWED_MESSAGE_KEY,
  });
};

// toFixed() uses scientific notation for abs >= 1e21; reconstruct the full
// decimal string manually when that happens.
function toFixedSafe(value: number, places: number): string {
  const result = value.toFixed(places);
  if (!result.includes('e') && !result.includes('E')) return result;
  const [coeff, expStr] = value.toExponential().split('e');
  const exp = parseInt(expStr);
  const sign = value < 0 ? '-' : '';
  const [intPart, fracPart = ''] = coeff.replace('-', '').split('.');
  const digits = intPart + fracPart;
  const intEnd = exp + 1;
  const fullInt = intEnd >= digits.length
    ? digits + '0'.repeat(intEnd - digits.length)
    : digits.slice(0, intEnd);
  return sign + fullInt + (places > 0 ? '.' + '0'.repeat(places) : '');
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  onBlur: onBlurProp,
  decimalPlaces = 3,
  placeholder,
  size,
  style = { textAlign: "right" },
  isInteger = false,
  positiveOnly = false,
  zeroIntegerOnly = false,
  maxIntegerDigits,
  disabled,
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const multiplier = Math.pow(10, decimalPlaces);

  // Update input value when prop value changes and input is not focused
  useEffect(() => {
    if (!isFocused) {
      setInputValue(toFixedSafe(value || 0, decimalPlaces));
    }
  }, [value, decimalPlaces, isFocused]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const el = e.currentTarget;

      // Enter moves to the same column, one row down (spreadsheet-style) — unlike
      // Tab, which keeps its native "next focusable element" order. Only walks
      // actual <tr>/<td> DOM structure, so it degrades to the old next-focusable
      // behavior outside a table (or once there's no row below and no next column
      // to wrap to).
      const focusableSelector = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])';
      let target: HTMLElement | null = null;
      let wrappedToNextColumn = false;
      const cell = el.closest('td, th') as HTMLTableCellElement | null;
      const row = cell?.closest('tr') ?? null;
      if (cell && row) {
        let nextRow = row.nextElementSibling as HTMLTableRowElement | null;
        while (nextRow && !target) {
          const nextCell = nextRow.cells[cell.cellIndex] as HTMLElement | undefined;
          target = nextCell?.querySelector<HTMLElement>(focusableSelector) ?? null;
          nextRow = nextRow.nextElementSibling as HTMLTableRowElement | null;
        }
        // Last row in the table — wrap to the next column, landing on that
        // column's topmost focusable cell (scanning top-down rather than
        // jumping straight to the table's first <tr>, since section-header/
        // sub-header rows have no per-column input and must be skipped —
        // same skip behavior as the same-column search above). Falling
        // through to the DOM-order fallback below instead would land on the
        // next column of this same last row.
        if (!target) {
          const tbody = row.parentElement;
          const maxCols = tbody
            ? Math.max(...Array.from(tbody.children).map((r) => (r as HTMLTableRowElement).cells.length))
            : 0;
          for (let colIndex = cell.cellIndex + 1; colIndex < maxCols && !target; colIndex++) {
            let scanRow = tbody!.firstElementChild as HTMLTableRowElement | null;
            while (scanRow && !target) {
              const nextCell = scanRow.cells[colIndex] as HTMLElement | undefined;
              target = nextCell?.querySelector<HTMLElement>(focusableSelector) ?? null;
              scanRow = scanRow.nextElementSibling as HTMLTableRowElement | null;
            }
          }
          wrappedToNextColumn = !!target;
        }
      }
      if (!target) {
        const focusable = Array.from(
          document.querySelectorAll<HTMLElement>(focusableSelector)
        );
        const idx = focusable.indexOf(el);
        if (idx >= 0 && idx + 1 < focusable.length) target = focusable[idx + 1];
      }

      el.blur(); // commits value via handleBlur — may trigger a re-render of the table
      target?.focus();
      // Wrapping to the next column lands on a row far above the one the user was
      // just looking at — scroll the page itself back to the top (same
      // `window.scrollTo({top: 0, left: 0, behavior})` used by `useScrollToTop`)
      // rather than `scrollIntoView`, which no-ops here since the sticky
      // header/columns these tables use keep the wrapped-to cell technically
      // "in view" without actually moving the page. Deferred a frame so it runs
      // after the blur-triggered re-render/focus above settle.
      if (wrappedToNextColumn) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        });
      }
      return;
    }

    if (positiveOnly && e.key === '-') {
      e.preventDefault();
      warnNegativeNotAllowed();
      return;
    }

    if (!positiveOnly || !zeroIntegerOnly) return;

    const prefix = '0.';
    const currentFrac = inputValue.startsWith(prefix) ? inputValue.slice(prefix.length) : '';
    const el = e.currentTarget;
    const selStart = el.selectionStart ?? inputValue.length;
    const selEnd = el.selectionEnd ?? inputValue.length;
    const hasSelection = selEnd > selStart;

    // Allow navigation and clipboard shortcuts to pass through unmodified.
    if (
      e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' ||
      e.key === 'Tab' || e.key === 'F5' ||
      ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'A', 'C', 'V', 'X', 'z', 'Z'].includes(e.key))
    ) {
      return;
    }

    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      if (hasSelection) {
        // Replace whatever was selected with this single digit.
        setInputValue(`${prefix}${e.key}`);
      } else if (currentFrac.length < decimalPlaces) {
        setInputValue(`${prefix}${currentFrac}${e.key}`);
      }
      // Fractional part is full — silently ignore extra digits.
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      setInputValue(hasSelection ? prefix : `${prefix}${currentFrac.slice(0, -1)}`);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      setInputValue(prefix);
    } else {
      // Block all other keys (letters, symbols, etc.).
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!positiveOnly || !zeroIntegerOnly) return;
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, decimalPlaces);
    setInputValue(`0.${digits}`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newValue = e.target.value;

    // zeroIntegerOnly: onKeyDown handles all char input; this is a safety net for mobile/IME.
    if (positiveOnly && zeroIntegerOnly) {
      const prefix = '0.';
      const fracRegex = new RegExp(`^\\d{0,${decimalPlaces}}$`);
      if (newValue.startsWith(prefix) && fracRegex.test(newValue.slice(prefix.length))) {
        setInputValue(newValue);
      } else {
        const digits = newValue.replace(/\D/g, '').slice(0, decimalPlaces);
        setInputValue(`${prefix}${digits}`);
      }
      return;
    }

    // Standard path
    const integerRegex = positiveOnly ? /^\d*$/ : /^-?\d*$/;
    const decimalRegex = positiveOnly
      ? positiveDecimalRegex(decimalPlaces, { zeroIntegerOnly, maxIntegerDigits })
      : new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}$`);

    const regex = isInteger ? integerRegex : decimalRegex;

    if (newValue === "" || (!positiveOnly && !isInteger && newValue === "-")) {
      setInputValue(newValue);
    } else if (regex.test(newValue)) {
      if (exceedsIntegerDigitCap(newValue)) {
        warnMaxDigits();
      } else {
        setInputValue(newValue);
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (disabled) return;

    let parsedValue: number;

    if (inputValue === "" || inputValue === "-" || inputValue === "." || inputValue === "0.") {
      parsedValue = 0;
    } else if (isInteger) {
      parsedValue = parseInt(inputValue) || 0;
    } else {
      parsedValue = parseFloat(inputValue) || 0;
      parsedValue = Math.round(parsedValue * multiplier) / multiplier;
    }

    setInputValue(toFixedSafe(parsedValue, decimalPlaces));
    onChange(parsedValue);
    onBlurProp?.();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsFocused(true);
    e.target.select();
  };

  return (
    <Input
      type="text"
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder || "0"}
      size={size}
      style={style}
      disabled={disabled}
      {...restProps}
    />
  );
};

export default NumberInput;
